import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import {
  ChatMessageRequest,
  Conversation,
  ConversationResponse,
  Message,
  TraceEvent,
} from '@/types';
import { useMutation } from '@/utils/api';

export function useCreateConversation() {
  const axios = useAxios();
  const { invalidate } = useMy({ resource: 'conversation' });

  return useMutation({
    defaultErrorMessage: 'Failed to create conversation',
    mutationFn: async (message: string): Promise<Conversation> => {
      const response = await axios.post<ConversationResponse>('/planner', {
        conversationId: '',
        message: message,
      });

      if (!response.data.conversation) {
        throw new Error('No conversation returned from server');
      }

      return response.data.conversation;
    },
    onSuccess: () => {
      invalidate();
    },
  });
}

export function useSendMessage() {
  const axios = useAxios();
  const { invalidate } = useMy({ resource: 'message' });
  const { invalidate: invalidateConversations } = useMy({
    resource: 'conversation',
  });

  return useMutation({
    defaultErrorMessage: 'Failed to send message',
    mutationFn: async (request: ChatMessageRequest): Promise<string> => {
      const response = await axios.post<ConversationResponse>('/planner', {
        conversationId: request.conversationId,
        message: request.message,
        mode: request.mode || 'query',
      });

      if (!response.data.response?.success) {
        throw new Error('Message failed to send');
      }

      return response.data.response.response;
    },
    onSuccess: () => {
      // Immediate refresh
      invalidate();
      invalidateConversations();

      // More aggressive polling for async backend processing results
      const pollTimes = [
        1000, 2000, 3000, 5000, 8000, 12000, 20000, 30000, 45000,
      ];
      pollTimes.forEach(delay => {
        setTimeout(() => {
          invalidate();
          invalidateConversations();
        }, delay);
      });
    },
  });
}

export function useGetConversations() {
  const { data, ...rest } = useMy({ resource: 'conversation' });

  // Sort conversations by created date (latest first)
  const sortedConversations = (data || []).sort(
    (a: Conversation, b: Conversation) => {
      return new Date(b.created).getTime() - new Date(a.created).getTime();
    }
  );

  return {
    data: sortedConversations as Conversation[],
    ...rest,
  };
}

export function useGetMessages(conversationId?: string) {
  // Only fetch messages if we have a conversation ID
  const { data, ...rest } = useMy(
    { resource: 'message', query: `#${conversationId}` },
    { enabled: !!conversationId } // Only run query if conversationId exists
  );

  const messages = (data || []) as Message[];

  return {
    data: messages.sort(
      (a, b) =>
        new Date(a.timestamp || '').getTime() -
        new Date(b.timestamp || '').getTime()
    ),
    ...rest,
  };
}

export function useGetConversationWithMessages(conversationId: string) {
  const { data: conversations, isLoading: conversationsLoading } =
    useGetConversations();
  const { data: messages, isLoading: messagesLoading } =
    useGetMessages(conversationId);

  const conversation = conversations.find(
    c =>
      c.uuid === conversationId ||
      c.id === conversationId ||
      c.key?.includes(conversationId)
  );

  return {
    conversation,
    messages,
    isLoading: conversationsLoading || messagesLoading,
  };
}

export function useGetConversationJobs(conversationId?: string) {
  const query = conversationId ? `conversation:${conversationId}` : '';

  const { data, isLoading, ...rest } = useMy(
    { resource: 'job', query },
    {
      enabled: !!conversationId,
      refetchInterval: 5000,
    }
  );

  const jobsArray = Array.isArray(data) ? data : [];

  return {
    data: jobsArray,
    isLoading,
    ...rest,
  };
}

export function useGetConversationTraces(traceId?: string) {
  const { data, isLoading, ...rest } = useMy(
    { resource: 'event', query: traceId || '' },
    {
      enabled: !!traceId,
      refetchInterval: 3000,
    }
  );

  // API returns {count, traceevents: [...]} - extract the array
  const tracesArray = Array.isArray(data)
    ? data
    : (data as { traceevents?: TraceEvent[] })?.traceevents || [];

  return {
    data: tracesArray as TraceEvent[],
    isLoading,
    ...rest,
  };
}
